// 각 메뉴에 해당하는 페이지 컴포넌트들
import React, { memo } from 'react';
import { PageContainer, PageTitle } from 'styles/common.styles';
import { HR_PAGE_REGISTRY } from '../../pages/hr/hrPageRegistry';

// 임시 컴포넌트 (컴포넌트를 찾을 수 없을 때 사용)
const PlaceholderPage = ({ title }) => (
  <PageContainer>
    <PageTitle>{title}</PageTitle>
  </PageContainer>
);

// require.context를 사용하여 pages 디렉토리의 모든 컴포넌트를 동적으로 로드
// webpack이 빌드 타임에 모든 가능한 모듈을 인식할 수 있도록 함
// PageComponents.js는 src/components/system/에 있으므로 ../../pages로 접근
const requireContext = require.context('../../pages', true, /\.js$/);

// 컴포넌트 맵 생성 (경로 -> 컴포넌트)
const componentMap = new Map();
// 메모이제이션된 컴포넌트 캐시 (경로 -> memo 컴포넌트)
const memoizedComponentMap = new Map();

// require.context로 등록된 모듈 + 레지스트리 병합 (신규 페이지 HMR/지연 추가 대응)
const syncComponentMap = () => {
  Object.entries(HR_PAGE_REGISTRY).forEach(([path, Component]) => {
    if (Component) componentMap.set(path, Component);
  });
  requireContext.keys().forEach((modulePath) => {
    const normalizedPath = modulePath
      .replace(/^\.\//, 'pages/')
      .replace(/\.js$/, '');
    if (normalizedPath.endsWith('/hrPageRegistry')) return;
    const module = requireContext(modulePath);
    const Component = module.default || module;
    if (Component) componentMap.set(normalizedPath, Component);
  });
};

syncComponentMap();

// 컴포넌트 동적 로드 함수
// component_path 형식: "pages/sso/stats001"
const loadComponent = (componentPath) => {
  if (!componentPath) {
    console.log('loadComponent: componentPath가 없습니다');
    return null;
  }

  // 정규화된 경로로 변환 (확장자 제거, 슬래시 정규화)
  const normalizedPath = componentPath
    .replace(/\.js$/, '')  // .js 확장자 제거
    .replace(/\\/g, '/');  // 백슬래시를 슬래시로 변환

  console.log(`loadComponent: 경로 조회 시도 (${normalizedPath})`);

  syncComponentMap();

  const Component = componentMap.get(normalizedPath);
  
  if (Component) {
    //console.log(`loadComponent: 컴포넌트 찾음 (${normalizedPath})`);
    return Component;
  } else {
    console.warn(`loadComponent: 컴포넌트를 찾을 수 없음 (${normalizedPath})`);
    // 유사한 경로 찾기 (대소문자 무시)
    const similarPath = Array.from(componentMap.keys()).find(path => 
      path.toLowerCase() === normalizedPath.toLowerCase()
    );
    if (similarPath) {
      console.log(`loadComponent: 유사한 경로 발견 (${similarPath})`);
      return componentMap.get(similarPath);
    }
    return null;
  }
};

// 컴포넌트 가져오기 함수
// componentPath가 있으면 동적으로 로드, 없으면 기존 방식 사용
// 반환되는 컴포넌트는 memo로 감싸져 있어 리렌더링 방지
// 같은 componentPath에 대해서는 항상 같은 memo 컴포넌트를 반환하도록 캐싱
export const getPageComponent = (componentName, componentPath) => {
  console.log('getPageComponent 호출:', { componentName, componentPath });
  
  // component_path가 있으면 동적 로드
  if (componentPath) {
    // 정규화된 경로로 변환 (캐시 키로 사용)
    const normalizedPath = componentPath
      .replace(/\.js$/, '')
      .replace(/\\/g, '/');
    
    // 이미 메모이제이션된 컴포넌트가 있으면 캐시에서 반환
    if (memoizedComponentMap.has(normalizedPath)) {
      console.log(`getPageComponent: 캐시에서 반환 (${normalizedPath})`);
      return memoizedComponentMap.get(normalizedPath);
    }
    
    const Component = loadComponent(componentPath);
    if (Component) {
      memoizedComponentMap.delete(`placeholder:${normalizedPath}`);
      // 컴포넌트를 memo로 감싸서 캐시에 저장
      const MemoizedComponent = memo(Component);
      MemoizedComponent.displayName = `Memoized(${Component.displayName || Component.name || 'Component'})`;
      
      // 캐시에 저장 (같은 경로에 대해서는 같은 memo 컴포넌트를 반환하도록)
      memoizedComponentMap.set(normalizedPath, MemoizedComponent);
      console.log(`getPageComponent: 새로 메모이제이션하고 캐시에 저장 (${normalizedPath})`);
      
      return MemoizedComponent;
    }
  }

  // 기존 방식 (componentName으로 매핑) 또는 componentPath가 없는 경우
  const placeholderKey = `placeholder:${componentPath || componentName}`;
  if (memoizedComponentMap.has(placeholderKey)) {
    return memoizedComponentMap.get(placeholderKey);
  }
  
  console.log('getPageComponent: PlaceholderPage 반환', { componentPath, componentName });
  const Placeholder = () => (
    <PlaceholderPage 
      title={`페이지를 찾을 수 없습니다 (${componentPath || componentName})`}
    />
  );
  const MemoizedPlaceholder = memo(Placeholder);
  memoizedComponentMap.set(placeholderKey, MemoizedPlaceholder);
  return MemoizedPlaceholder;
};

